// ─── Buy-box evaluator ──────────────────────────────────────────────────────
// Pure function. Given a rule (a JSON blob shaped like the one seeded in
// scripts/2026-08-17-phase2-buybox.sql) and a candidate row (a deal or a
// property), returns { verdict, reason }.
//
// Verdicts:
//   'undecided' — the rule is empty in every dimension; nothing to grade against.
//   'in_box'    — every specified dimension matched.
//   'filtered'  — at least one specified dimension failed. Reason lists them
//                 all (comma-separated) so the user can see why.
//
// The evaluator does not touch the database and does not know about "manual"
// overrides — that logic lives in the write hooks (see supabase.js). A row
// with buy_box_source='manual' must be skipped by the caller so the auto
// verdict doesn't clobber a human decision.
//
// Row shape expected:
//   { msas: string[], product_type: string, squareFeet: number, capRate: number }
// Property rows use `size_sf` instead of `squareFeet` and don't have a cap rate,
// so callers can pre-map them into this shape.

// Convert legacy free-text market strings ("South Florida") into a synthetic
// msas[] with the same value, so old rows still evaluate. New rows always
// have real msas[] populated by the Phase 1 auto-tagger.
function coerceMsas(row) {
  if (Array.isArray(row.msas) && row.msas.length > 0) return row.msas;
  if (typeof row.market === "string" && row.market.trim())
    return row.market.split(",").map(s => s.trim()).filter(Boolean);
  return [];
}

// Read square footage from whichever field the row happens to use. Deals
// carry `squareFeet` (camelCase from dbToDeal); properties carry `size_sf`
// (snake_case straight from the DB).
function coerceSf(row) {
  const n = Number(row.squareFeet ?? row.size_sf ?? row.building_sf);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function coerceCapRate(row) {
  const n = Number(row.capRate ?? row.cap_rate);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function coerceYearBuilt(row) {
  const n = Number(row.yearBuilt ?? row.year_built);
  return Number.isFinite(n) && n > 1800 ? Math.round(n) : null;
}

function coerceClearHeight(row) {
  const n = Number(row.clearHeight ?? row.clear_height);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Returns true if every dimension in the rule is empty/null. Used to short-
// circuit to 'undecided' — a rule with nothing to check shouldn't filter
// anyone out. If Daniel adds new dimensions to the rule schema, update this
// list so an "all-empty" rule keeps its no-op behavior.
function ruleIsEmpty(rule) {
  if (!rule || typeof rule !== "object") return true;
  const anyMsa    = Array.isArray(rule.msas) && rule.msas.length > 0;
  const anyType   = Array.isArray(rule.product_types) && rule.product_types.length > 0;
  const anySfMin  = rule.sf_min != null;
  const anySfMax  = rule.sf_max != null;
  const anyCap    = rule.cap_rate_min != null;
  const anyYear   = rule.year_built_min != null;
  const anyHeight = rule.clear_height_min != null;
  return !(anyMsa || anyType || anySfMin || anySfMax || anyCap || anyYear || anyHeight);
}

export function evaluateBuyBox(rule, row) {
  if (ruleIsEmpty(rule)) return { verdict: "undecided", reason: null };

  const fails = [];

  // ── MSA whitelist ────────────────────────────────────────────────
  if (Array.isArray(rule.msas) && rule.msas.length > 0) {
    const rowMsas = coerceMsas(row);
    const whitelist = new Set(rule.msas);
    const hit = rowMsas.some(m => whitelist.has(m));
    if (!hit) fails.push(rowMsas.length === 0 ? "no MSA" : `MSA (${rowMsas.join(", ")})`);
  }

  // ── Product type whitelist ───────────────────────────────────────
  if (Array.isArray(rule.product_types) && rule.product_types.length > 0) {
    const pt = (row.product_type || "").trim();
    if (!pt) fails.push("no product type");
    else if (!rule.product_types.includes(pt)) fails.push(`product type (${pt})`);
  }

  // ── Square footage band ──────────────────────────────────────────
  const sf = coerceSf(row);
  if (rule.sf_min != null || rule.sf_max != null) {
    if (sf == null) {
      fails.push("no SF");
    } else {
      if (rule.sf_min != null && sf < rule.sf_min)
        fails.push(`SF ${sf.toLocaleString()} < ${Number(rule.sf_min).toLocaleString()}`);
      if (rule.sf_max != null && sf > rule.sf_max)
        fails.push(`SF ${sf.toLocaleString()} > ${Number(rule.sf_max).toLocaleString()}`);
    }
  }

  // ── Cap rate floor (skipped for properties, which don't carry one) ──
  if (rule.cap_rate_min != null) {
    const cap = coerceCapRate(row);
    // Properties genuinely don't have a cap rate — don't penalize them.
    // Deals without a cap rate are pre-underwriting and stay undecided
    // on this dimension too, rather than being marked filtered.
    if (cap != null && cap < rule.cap_rate_min)
      fails.push(`cap ${cap}% < ${rule.cap_rate_min}%`);
  }

  // ── Vintage floor (year_built_min) ───────────────────────────────
  // Missing year_built is not a fail — old CoStar rows without vintage
  // stay in-box on this dimension. Adjust here if we ever want to be
  // stricter.
  if (rule.year_built_min != null) {
    const y = coerceYearBuilt(row);
    if (y != null && y < rule.year_built_min)
      fails.push(`built ${y} < ${rule.year_built_min}`);
  }

  // ── Clear height floor ───────────────────────────────────────────
  // Same "missing = pass" treatment as vintage.
  if (rule.clear_height_min != null) {
    const h = coerceClearHeight(row);
    if (h != null && h < rule.clear_height_min)
      fails.push(`clear ${h}' < ${rule.clear_height_min}'`);
  }

  if (fails.length === 0) return { verdict: "in_box", reason: null };
  return { verdict: "filtered", reason: fails.join("; ") };
}

// Product type options — kept here so the Settings buy-box panel and the
// deal edit form pull from the same list. Adjust freely; new values just
// need to match what's stored in deals.product_type.
export const PRODUCT_TYPES = [
  { value: "bulk",         label: "Bulk / Big-box" },
  { value: "shallow_bay",  label: "Shallow-bay" },
  { value: "flex",         label: "Flex" },
  { value: "ios",          label: "IOS (Industrial Outdoor Storage)" },
  { value: "cold_storage", label: "Cold storage" },
  { value: "other",        label: "Other" },
];
